import React from "react";
import { Alert, AlertColor, Box, Typography } from "@mui/material";
import { ProcessState } from "types/process";
import { CircularProgressWithLabel } from "components";
import { CircularProgress } from "@mui/material";

export interface ProcessStatusPanelProps {
  processState: ProcessState | null | undefined;
  percentComplete?: number;

  /** Message area content: string OR React node(s) */
  message?: React.ReactNode;

  /** Heading text when the process is running */
  inProgressTitle?: string;
  /** Heading text when the process completes successfully */
  successTitle?: string;
  /** Heading text when the process fails */
  errorTitle?: string;
}

type StatusKind = "none" | "inProgress" | "success" | "error";

type TitleKey = "inProgressTitle" | "successTitle" | "errorTitle";

interface ProcessStatusConfig {
  kind: StatusKind;
  severity?: AlertColor;
  /** Which title prop to use (inProgressTitle / successTitle / errorTitle) */
  titleKey?: TitleKey;
  /** What type of icon to show */
  iconType?: "spinner" | "progressWithLabel";
}

/**
 * Map a ProcessState + percent-validity into a config object
 */
export const getProcessStatusConfig = (
  processState: ProcessState | null | undefined,
  hasValidPercentComplete: boolean
): ProcessStatusConfig => {
  if (
    processState === null ||
    processState === undefined ||
    processState === ProcessState.IDLE
  ) {
    return { kind: "none" };
  }

  switch (processState) {
    case ProcessState.INITIALIZING:
      return {
        kind: "inProgress",
        severity: "info",
        titleKey: "inProgressTitle",
        iconType: "spinner",
      };

    case ProcessState.QUEUED:
    case ProcessState.PROCESSING:
      return {
        kind: "inProgress",
        severity: "info",
        titleKey: "inProgressTitle",
        iconType: hasValidPercentComplete ? "progressWithLabel" : "spinner",
      };

    case ProcessState.COMPLETE:
      return {
        kind: "success",
        severity: "success",
        titleKey: "successTitle",
      };

    case ProcessState.ERROR:
      return {
        kind: "error",
        severity: "error",
        titleKey: "errorTitle",
      };

    default:
      return { kind: "none" };
  }
};

/**
 * Optional helper if you want a simple “default title per state” function.
 */
export const getDefaultTitleForProcessState = (
  processState: ProcessState
): string => {
  switch (processState) {
    case ProcessState.INITIALIZING:
    case ProcessState.QUEUED:
    case ProcessState.PROCESSING:
      return "In Progress";
    case ProcessState.COMPLETE:
      return "Success";
    case ProcessState.ERROR:
      return "Error";
    default:
      return "";
  }
};

export const ProcessStatusPanel: React.FC<ProcessStatusPanelProps> = ({
  processState,
  percentComplete,
  message,
  inProgressTitle = "In Progress",
  successTitle = "Success",
  errorTitle = "Error",
}) => {
  const hasValidPercentComplete =
    typeof percentComplete === "number" &&
    !Number.isNaN(percentComplete) &&
    percentComplete >= 0 &&
    percentComplete <= 100;

  // Get the per-state config
  const config = getProcessStatusConfig(processState, hasValidPercentComplete);

  // When idle or no process state is supplied, render nothing
  if (config.kind === "none" || !config.severity) {
    return null;
  }

  // Resolve which title string to use based on config.titleKey
  const title: string =
    (config.titleKey === "inProgressTitle" && inProgressTitle) ||
    (config.titleKey === "successTitle" && successTitle) ||
    (config.titleKey === "errorTitle" && errorTitle) ||
    "";

  // Build the correct icon based on config.iconType
  let icon: React.ReactNode | undefined;

  if (config.iconType === "spinner") {
    icon = <CircularProgress />;
  } else if (
    config.iconType === "progressWithLabel" &&
    hasValidPercentComplete
  ) {
    icon = (
      <CircularProgressWithLabel size={50} value={percentComplete as number} />
    );
  }

  return (
    <Box width="100%" display="flex" justifyContent="center">
      <Alert
        severity={config.severity}
        variant="outlined"
        icon={icon}
        sx={{ width: "100%" }}
      >
        {title && <strong>{title}</strong>}

        {message && (
          <Typography component="div" sx={{ mt: 0.5 }}>
            {message}
          </Typography>
        )}
      </Alert>
    </Box>
  );
};
